import { defer, from, Observable, shareReplay, throwError } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { LOCALSTORAGE_KEY } from "@config";
import type { HttpErrorShape, HttpPlugin, HttpRequestConfig } from "@kalodata/http";
import { requestRefreshAccessToken } from "./refreshTokenRequest";

const defaultAttachToken = <TMeta, TContext>(
  request: HttpRequestConfig<unknown, TMeta, TContext>,
  token: string,
): HttpRequestConfig<unknown, TMeta, TContext> => {
  return {
    ...request,
    headers: {
      ...request.headers,
      Authorization: `Bearer ${token}`,
    },
  };
};

const defaultShouldRefresh = <TMeta, TContext>(error: HttpErrorShape<unknown, TMeta, TContext>) => {
  return error.status === 401 && (error.response?.data as { code?: string } | undefined)?.code === "TOKEN_EXPIRED";
};

const getToken = () => localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);

const resolveRefreshToken = async () => {
  const accessToken = await requestRefreshAccessToken();
  if (accessToken) {
    localStorage.setItem(LOCALSTORAGE_KEY.ACCESS_TOKEN, accessToken);
  }
  return accessToken;
};

const redirectToLogin = () => {
  if (["/login", "/signup", "/authorization", "/privacy-policy"].includes(window.location.pathname)) return;
  const needSignUp = !localStorage.getItem("userName");
  const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = needSignUp ? `/signup?redirectUrl=${redirectUrl}` : `/login?redirectUrl=${redirectUrl}`;
};

export const createAuthPlugin = <TMeta = unknown, TContext = unknown>(): HttpPlugin<TMeta, TContext> => {
  let refreshingRequest$: Observable<string | undefined | null> | null = null;

  const getRefreshingRequest$ = (): Observable<string | undefined | null> => {
    if (refreshingRequest$) return refreshingRequest$;
    refreshingRequest$ = defer(() => from(resolveRefreshToken())).pipe(
      finalize(() => {
        refreshingRequest$ = null;
      }),
      shareReplay(1),
    );
    return refreshingRequest$;
  };

  const handleAuthFailed = (error: HttpErrorShape<unknown, TMeta, TContext>) => {
    redirectToLogin();
    return throwError(() => error);
  };

  return {
    name: "auth",
    onRequest: async (request) => {
      const token = getToken();
      if (!token) return request;
      return defaultAttachToken(request, token);
    },
    onError: (error, context) => {
      if (!defaultShouldRefresh(error)) {
        if (error.status === 401) {
          return handleAuthFailed(error);
        }
        throw error;
      }
      return getRefreshingRequest$().pipe(
        switchMap((token) => {
          if (!token) {
            return handleAuthFailed(error);
          }
          return context.retry(defaultAttachToken(context.request, token));
        }),
        catchError(handleAuthFailed),
      );
    },
  };
};
